import React, { useEffect, useMemo, useState } from "react";
import { API_BASE } from "../api/config";
import LocationSelect from "../components/LocationSelect";
import "./FinanceTrendPanel.css";

const SHOPS = [
  "ALL",
  "GMP",
  "OSHT",
  "OSPF",
  "MT",
  "JS",
  "JCHT",
  "JCPF",
  "MDYHT",
  "MDYPF",
];

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function formatMoney(value) {
  return Number(value || 0).toLocaleString();
}

export default function FinanceTrendPanel() {
  const [shop, setShop] = useState("ALL");
  const [days, setDays] = useState("7");
  const [dateTo, setDateTo] = useState(todayKey());
  const [rows, setRows] = useState([]);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  async function loadTrend() {
    try {
      setLoading(true);
      setError("");

      const res = await fetch(
        `${API_BASE}/api/erp/finance-trend?shop=${shop}&days=${days}&dateTo=${dateTo}`,
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to load finance trend");
      }

      setRows(data.rows || []);
    } catch (err) {
      setError(err.message || "Finance trend loading failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadTrend();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shop, days, dateTo]);

  const totals = useMemo(() => {
    return rows.reduce(
      (acc, row) => {
        acc.cashIn += Number(row.cashIn || 0);
        acc.cashOut += Number(row.cashOut || 0);
        acc.netCash += Number(row.netCash || 0);
        acc.expenseTotal += Number(row.expenseTotal || 0);
        return acc;
      },
      {
        cashIn: 0,
        cashOut: 0,
        netCash: 0,
        expenseTotal: 0,
      },
    );
  }, [rows]);

  const latest = rows[rows.length - 1] || null;
  const previous = rows[rows.length - 2] || null;

  const latestNet = Number(latest?.netCash || 0);
  const previousNet = Number(previous?.netCash || 0);
  const netChange = latestNet - previousNet;

  return (
    <div className="financeTrend">
      <div className="financeTrend__head">
        <div>
          <span>Finance Trend</span>
          <h2>{days} Days Cash Movement</h2>
          <p>Boss view for cash in, cash out, net cash and expense movement.</p>
        </div>

        <div className="financeTrend__filters">
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
          />

          <LocationSelect value={shop} onChange={setShop} mode="shop" includeAll />

          <select value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="7">Last 7 Days</option>
            <option value="14">Last 14 Days</option>
            <option value="30">Last 30 Days</option>
          </select>

          <button onClick={loadTrend} disabled={loading}>
            {loading ? "Loading..." : "Refresh"}
          </button>
        </div>
      </div>

      {error && <div className="financeTrend__error">{error}</div>}

      <div className="financeTrend__cards">
        <div className="trendMiniCard">
          <span>Total Cash In</span>
          <strong>{formatMoney(totals.cashIn)} Ks</strong>
        </div>

        <div className="trendMiniCard">
          <span>Total Cash Out</span>
          <strong>{formatMoney(totals.cashOut)} Ks</strong>
        </div>

        <div className="trendMiniCard">
          <span>Total Net Cash</span>
          <strong>{formatMoney(totals.netCash)} Ks</strong>
        </div>

        <div className="trendMiniCard">
          <span>Latest Net Change</span>
          <strong className={netChange >= 0 ? "trendGood" : "trendBad"}>
            {netChange >= 0 ? "+" : ""}
            {formatMoney(netChange)} Ks
          </strong>
        </div>
      </div>

      <div className="financeTrendTableWrap">
        <table className="financeTrendTable">
          <thead>
            <tr>
              <th>Date</th>
              <th>Cash In</th>
              <th>Cash Out</th>
              <th>Net Cash</th>
              <th>Expense</th>
              <th>Status</th>
            </tr>
          </thead>

          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan="6" className="financeTrendTable__empty">
                  No finance trend data.
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const net = Number(row.netCash || 0);

                return (
                  <tr key={row.date}>
                    <td>{row.date}</td>
                    <td>{formatMoney(row.cashIn)} Ks</td>
                    <td>{formatMoney(row.cashOut)} Ks</td>
                    <td>
                      <strong className={net >= 0 ? "trendGood" : "trendBad"}>
                        {formatMoney(net)} Ks
                      </strong>
                    </td>
                    <td>{formatMoney(row.expenseTotal)} Ks</td>
                    <td>
                      <span
                        className={
                          net >= 0
                            ? "trendBadge trendBadge--good"
                            : "trendBadge trendBadge--bad"
                        }
                      >
                        {net >= 0 ? "GOOD" : "WATCH"}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


